import { useEffect, useMemo, useState } from 'react';
import { FilterModel, IServerSideDatasource, RowModelType } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { size } from 'lodash';

import { STALE_TIME } from '@blockworks/platform/api';
import {
    GetExecutionRequest,
    GetExecutionResponse,
    StudioExecutionColumnTypes,
    studioExecutionApi,
} from '@blockworks/platform/api/research/studio';

import { useVisualizationOptionsContext } from '../../../../../../context';
import { VisualizationTableProps } from '../../models';
import { SYNTHETIC_GLOBAL_SEARCH_COL } from '../make-columns';

type UseVisualizationTableProps = Pick<VisualizationTableProps, 'data' | 'meta' | 'executionId' | 'options' | 'id'> & {
    columnTypes: Record<string, StudioExecutionColumnTypes>;
    setColumnTypes: (columnTypes: Record<string, StudioExecutionColumnTypes>) => void;
    gridRef: React.MutableRefObject<AgGridReact | null>;
};

type TableData = Omit<Partial<GetExecutionResponse['data']>, 'metadata'> & {
    metadata: Partial<GetExecutionResponse['data']['metadata']>;
};

const useDatasource = ({
    data: preloadedData,
    meta,
    executionId,
    columnTypes,
    setColumnTypes,
    options,
    gridRef,
    id,
}: UseVisualizationTableProps) => {
    const [fetchedColumnTypes, setFetchedColumnTypes] = useState<Record<string, StudioExecutionColumnTypes>>({});
    const { features } = useVisualizationOptionsContext();

    const rowModelType: RowModelType = useMemo(
        () => (!features.tablePagination ? 'clientSide' : 'serverSide'),
        [features],
    );

    const { data: clientSideData } = studioExecutionApi.useGetExecution({
        variables: {
            id: executionId,
            proxyUri: features.getExecutionProxyUri?.({ visualizationId: id }),
        },
        initialData: {
            data: {
                rows: preloadedData,
            },
        } as any,
        staleTime: STALE_TIME.$5Minutes,
        enabled: !features.tablePagination,
        select: ({ data }) => {
            const allRows = data.rows;
            return {
                rows: allRows.slice(0, options?.pageSize ?? 25),
                totalRowsFound: meta?.totalRowsFound ?? allRows.length ?? 0,
            };
        },
    });

    useEffect(() => {
        if (!!features.tablePagination || !gridRef.current?.api?.setGridOption) return;

        gridRef.current.api.setGridOption('context', {
            totalRows: clientSideData?.totalRowsFound,
            isClientSide: true,
        });
    }, [features.tablePagination, gridRef, gridRef.current?.api?.setGridOption, clientSideData]);

    useEffect(() => {
        if (size(columnTypes)) return;
        if (size(meta?.columnTypes) || size(fetchedColumnTypes)) {
            setColumnTypes(meta?.columnTypes ?? fetchedColumnTypes);
        }
    }, [fetchedColumnTypes, setColumnTypes, meta?.columnTypes, columnTypes]);

    const serverSideDatasource: IServerSideDatasource | undefined = useMemo(() => {
        if (!features.tablePagination) return;
        return {
            getRows: async ({ success, fail, request }) => {
                const { endRow, startRow, sortModel, filterModel } = request;
                const pageSize = (endRow ?? 0) - (startRow ?? 0);
                const nextPage = (startRow ?? 0) / pageSize + 1;

                let order: GetExecutionRequest['order'] | undefined;
                const sortParams = sortModel[0];
                if (sortParams?.colId && sortParams?.sort) {
                    order = `${sortParams.colId},${sortParams.sort}`;
                }

                const globalSearch = (filterModel as FilterModel)?.[SYNTHETIC_GLOBAL_SEARCH_COL.colId];
                const preloadedDataSize = preloadedData?.length ?? 0;
                let tableData: TableData = { metadata: { totalRowsFound: 0 } };

                if (
                    preloadedDataSize &&
                    nextPage < 2 &&
                    !globalSearch?.filter &&
                    !order &&
                    (preloadedDataSize >= options?.pageSize || preloadedDataSize >= (meta?.totalRowsFound ?? 0))
                ) {
                    tableData['rows'] = preloadedData;
                    tableData.metadata.totalRowsFound = meta?.totalRowsFound ?? preloadedDataSize;
                } else {
                    try {
                        const res = await studioExecutionApi.useGetExecution.fetcher({
                            id: executionId,
                            page: nextPage,
                            limit: options?.pageSize ? +options?.pageSize : undefined,
                            order,
                            filter: globalSearch?.filter?.toLowerCase(),
                            proxyUri: features.getExecutionProxyUri?.({ visualizationId: id }),
                        });
                        tableData = res.data;
                    } catch (error) {
                        console.error('Error fetching execution data for table:', error);
                        return fail();
                    }
                }

                if (tableData?.metadata?.columnTypes) {
                    setFetchedColumnTypes(tableData.metadata.columnTypes);
                }

                if (tableData?.rows) {
                    return success({
                        rowData: tableData?.rows,
                        rowCount: tableData?.metadata?.totalRowsFound || tableData?.rows?.length || 0,
                    });
                }

                console.error('Error using execution rows with table:', tableData);
                fail();
            },
        };
    }, [
        preloadedData,
        options?.pageSize,
        meta?.totalRowsFound,
        executionId,
        features.tablePagination,
        features.getExecutionProxyUri,
    ]);

    const clientSideRowData = useMemo(() => {
        if (!clientSideData?.rows) return [];
        return [...clientSideData.rows]; // Create a new array to avoid reference issues
    }, [clientSideData?.rows]);

    return {
        clientSideRowData,
        serverSideDatasource,
        rowModelType,
    };
};

export type { UseVisualizationTableProps };
export { useDatasource };
